<script setup>
import moment from 'moment'
import axios from '@axios'
import { useUsersStore, useReservationsStore, useRestaurantsStore } from '@/stores'
import { numberWithCommas } from '@/plugins/misc'
import { onBeforeMount, onBeforeUnmount, computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CommsHistoryPanel from '@core/components/CommsHistoryPanel.vue'
import { useRoute } from 'vue-router'
import { approveRows, incomingKeepsApprove, isApproveRow } from '@/utils/adminReservationBuckets'

// Initialize stores
const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()

const {
  myUser
} = storeToRefs(usersStore)

const {
  inReviewReservations,
  completeReservations,
  fetching,
  completedAnalytics,
  analyticsLoading,
  lastStatusChangeNotifiedAt,
} = storeToRefs(reservationsStore)

// Filter state
const filterRestaurantType = ref('All')
const filterRestaurantId = ref(null)
const filterRestaurantGroup = ref(null)
const filterCity = ref(null)

// Parent tabs: "in-progress", "completed", or "stats" — default to Stats so the
// analytics cards show by default; the "In Progress" tab shows a badge when
// there's work to do. Honor an optional `?tab=` query (e.g. /admin/bookings?tab=stats)
// so deep-links from the admin profile page can pick the initial tab explicitly.
const route = useRoute()
const validParentTabs = ['in-progress', 'completed', 'stats']
const initialParentTab = validParentTabs.includes(route.query?.tab) ? route.query.tab : 'stats'
const parentTab = ref(initialParentTab)

// Sub-tabs for each parent
const inProgressSubTab = ref('awaiting response')
const completedSubTab = ref('Experienced')

// Tab-specific time period state. Stats and Completed each remember their own
// period selection so filtering one doesn't disturb the other.
const statsPeriod = ref('All')
const statsCustomStartDate = ref(null)
const statsCustomEndDate = ref(null)

const completedPeriod = ref('YTD')
const completedCustomStartDate = ref(null)
const completedCustomEndDate = ref(null)

// Proxies that read/write the active tab's period state. Keeps the existing
// `period.value = 'All'` and watchers below working unchanged.
const period = computed({
  get: () => parentTab.value === 'stats' ? statsPeriod.value : completedPeriod.value,
  set: value => {
    if (parentTab.value === 'stats') statsPeriod.value = value
    else completedPeriod.value = value
  },
})
const customStartDate = computed({
  get: () => parentTab.value === 'stats' ? statsCustomStartDate.value : completedCustomStartDate.value,
  set: value => {
    if (parentTab.value === 'stats') statsCustomStartDate.value = value
    else completedCustomStartDate.value = value
  },
})
const customEndDate = computed({
  get: () => parentTab.value === 'stats' ? statsCustomEndDate.value : completedCustomEndDate.value,
  set: value => {
    if (parentTab.value === 'stats') statsCustomEndDate.value = value
    else completedCustomEndDate.value = value
  },
})

// Track if we've loaded complete data
const completeTabsLoaded = ref(false)

// Separate readiness flag for the completed reservation list. The store's
// shared `fetching` flag is also toggled by in-review fetches and can go
// false while completeReservations is still in flight, so we track our own.
const completeReservationsReady = ref(false)

async function loadCompleteReservations() {
  try {
    await reservationsStore.getAllCompleteReservations(earliestDate.value, latestDate.value)
  } finally {
    completeReservationsReady.value = true
  }
}

// Single source of truth for loading completed analytics + reservation list.
// Drives the initial fetch from onBeforeMount (after myUser resolves) and the
// three watches below. Avoids the mount-time race where concurrent fetches for
// different periods could leave the wrong result in the store.
function ensureCompletedDataLoaded() {
  if (parentTab.value !== 'completed' && parentTab.value !== 'stats') return
  if (period.value === 'Custom' && (!customStartDate.value || !customEndDate.value)) return

  if (!completeTabsLoaded.value) {
    reservationsStore.setupCompleteListeners()
    completeTabsLoaded.value = true
  }
  completeReservationsReady.value = false
  loadCompleteReservations()
  reservationsStore.getCompletedAnalytics(earliestDate.value, latestDate.value)
}

// Restaurant-admin scope: when the caller is a restaurant admin (not a platform
// admin), the Restaurant dropdown and Restaurant Group filter are restricted to
// (or hidden for) the restaurants they actually manage.
const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')
const accessibleRestaurantIds = ref(null) // null = not yet loaded; [] = none; [ids] = scope
const canExportCsv = computed(() =>
  isPlatformAdmin.value
  || (Array.isArray(accessibleRestaurantIds.value) && accessibleRestaurantIds.value.length > 0),
)

async function loadRestaurantScope() {
  try {
    const { data } = await axios.get('/user/me/restaurant-scope')
    const ids = Array.isArray(data?.restaurants)
      ? data.restaurants.map(r => String(r._id)).filter(Boolean)
      : []
    accessibleRestaurantIds.value = ids
  } catch {
    accessibleRestaurantIds.value = []
  }
}

// Cities with at least one real request, ever — loaded once, independent of
// the Time period selector, so the City filter's options don't reactively
// shrink/grow as the analytics date range changes.
const requestedCities = ref([])

async function loadRequestedCities() {
  try {
    const { data } = await axios.get('/reservation/requested-cities')
    requestedCities.value = Array.isArray(data) ? data : []
  } catch {
    requestedCities.value = []
  }
}

// Wait for myUser to be populated — may already be cached, or the users store
// may still be fetching it.
function waitForUser() {
  if (myUser.value) return Promise.resolve()

  return new Promise(resolve => {
    const stop = watch(myUser, user => {
      if (user) {
        stop()
        resolve()
      }
    }, { immediate: true })
  })
}

// Hook into the event when navigating to the view
onBeforeMount(async () => {
  console.log('Navigating to the view')

  // Always fetch fresh data when mounting this page
  if (reservationsStore) {
    reservationsStore.getInReviewReservations()
    reservationsStore.setupInReviewListeners()
  }

  // Load restaurants for filter dropdowns
  if (!restaurantsStore.hasFetchedRestaurantList) {
    await restaurantsStore.getRestaurants()
  }

  // Load the caller's restaurant scope so we can filter dropdowns for
  // non-platform-admin restaurant admins.
  await loadRestaurantScope()

  // Load the City filter's options — fire-and-forget, doesn't block the rest
  // of mount (matches the pattern used for restaurant list fetch below).
  loadRequestedCities()

  // Wait for myUser, then apply the period default before any fetch fires —
  // avoids the mount-time period-vs-fetch race. Platform admins default to
  // "Past 3 Months" so the dashboard isn't trying to render years of history
  // on every tab open; restaurant admins default to "All time" since their
  // scoped lists are small. The matching period watcher only fires when the
  // value actually changes, so suppress the explicit load when an override is
  // about to flip the value (avoids duplicate fetches).
  await waitForUser()
  const defaultPeriod = myUser.value.userLevel === 'admin' ? 'Past 3 Months' : 'All'
  let needsExplicitLoad = parentTab.value === 'completed' || parentTab.value === 'stats'
  if (parentTab.value === 'stats' && statsPeriod.value !== defaultPeriod) needsExplicitLoad = false
  if (parentTab.value === 'completed' && completedPeriod.value !== defaultPeriod) needsExplicitLoad = false
  statsPeriod.value = defaultPeriod
  completedPeriod.value = defaultPeriod
  if (needsExplicitLoad) {
    ensureCompletedDataLoaded()
  }
})

// Hook into the event when navigating away from the view
onBeforeUnmount(() => {
  console.log('Navigating away from the view')
  if (reservationsStore) {
    reservationsStore.removeInReviewListeners()
    reservationsStore.removeCompleteListeners()
  }
})

// User switches to the Completed or Stats tab
watch(parentTab, newValue => {
  if (newValue === 'completed' || newValue === 'stats') {
    ensureCompletedDataLoaded()
  }
})

// Period changes — only refetch when the change affects the active tab.
// (Custom-without-dates is guarded inside the helper.)
watch(statsPeriod, () => {
  if (parentTab.value === 'stats') ensureCompletedDataLoaded()
})
watch(completedPeriod, () => {
  if (parentTab.value === 'completed') ensureCompletedDataLoaded()
})

// Custom date changes — same active-tab guard.
watch([statsCustomStartDate, statsCustomEndDate], () => {
  if (parentTab.value === 'stats' && statsPeriod.value === 'Custom') {
    ensureCompletedDataLoaded()
  }
})
watch([completedCustomStartDate, completedCustomEndDate], () => {
  if (parentTab.value === 'completed' && completedPeriod.value === 'Custom') {
    ensureCompletedDataLoaded()
  }
})

// Realtime push: another tab (typically the response page) accepted/declined
// a reservation. Refetch the lists currently visible so the dashboard isn't
// stuck on a stale status.
watch(lastStatusChangeNotifiedAt, () => {
  reservationsStore.getInReviewReservations()
  if ((parentTab.value === 'completed' || parentTab.value === 'stats') && completeTabsLoaded.value) {
    ensureCompletedDataLoaded()
  }
})

// Pre-fetch fan-card data for all loaded reservation owners in batches so the
// reservation list doesn't fire one request per card on render — the per-card
// `GET /restaurant-admin/fans/:id` flood was exhausting Chrome's connection
// pool whenever a large list (e.g. Completed) rendered.
const fanDataMap = ref({})
const fanDataInFlight = new Set()
const FAN_BATCH_CHUNK_SIZE = 200

async function fetchFanDataChunk(ownerIds) {
  if (ownerIds.length === 0) return
  ownerIds.forEach(id => fanDataInFlight.add(id))
  try {
    const { data } = await axios.post('/restaurant-admin/fans/batch', { memberUserIds: ownerIds })
    const fans = data?.fans || {}
    const merged = { ...fanDataMap.value }
    ownerIds.forEach(id => {
      merged[id] = fans[id] || { accessDenied: true }
    })
    fanDataMap.value = merged
  } catch {
    // Cards fall back to per-card fetch when prop stays undefined.
  } finally {
    ownerIds.forEach(id => fanDataInFlight.delete(id))
  }
}

function prefetchFanData(reservations) {
  if (!Array.isArray(reservations)) return

  // Platform admins don't see the fan-of / bookings-via-RM block in the card,
  // so don't pay for the lookup.
  if (isPlatformAdmin.value) return
  const ids = []
  const seen = new Set()
  for (const r of reservations) {
    const raw = r?.owner?._id
    const id = raw == null ? '' : String(raw)
    if (!id || seen.has(id)) continue
    seen.add(id)
    if (fanDataMap.value[id] || fanDataInFlight.has(id)) continue
    ids.push(id)
  }
  for (let i = 0; i < ids.length; i += FAN_BATCH_CHUNK_SIZE) {
    fetchFanDataChunk(ids.slice(i, i + FAN_BATCH_CHUNK_SIZE))
  }
}

watch(inReviewReservations, list => prefetchFanData(list || []), { immediate: true })
watch(completeReservations, list => prefetchFanData(list || []), { immediate: true })

// "Open response page" links only make sense for reservations that still
// need a restaurant response (In Progress) or that have been accepted and are
// still ahead (Completed → Upcoming). An Approve? row waits on RM, not on a
// restaurant's response, so it offers none.
const showResponseLinks = computed(() => {
  if (parentTab.value === 'in-progress') return inProgressSubTab.value !== 'Approve?'

  return parentTab.value === 'completed' && completedSubTab.value === 'Upcoming'
})

// Get the reservations currently visible based on active tab + filters
const currentVisibleReservations = computed(() => {
  if (parentTab.value === 'in-progress') {
    switch (inProgressSubTab.value) {
    case 'awaiting response': return reservationsAwaitingResponse.value
    case 'Approve?': return reservationsApprove.value
    case 'in review': return reservationsInReview.value
    case 'member action': return reservationsMemberAction.value
    case 'approval needed': return reservationsApprovalNeeded.value
    case 'upcoming': return reservationsUpcoming.value
    default: return []
    }
  } else {
    switch (completedSubTab.value) {
    case 'Experienced': return reservationsExperienced.value
    case 'Upcoming': return reservationsUpcoming.value
    case 'Not Available': return reservationsNotAvailable.value
    case 'No Response': return reservationsNoResponse.value
    case 'Declined': return reservationsDeclined.value
    case 'Rescinded': return reservationsRescinded.value
    case 'Canceled': return reservationsCanceled.value
    case 'Submitted': return reservationsIncoming.value
    default: return []
    }
  }
})

function escapeCsvField(value) {
  if (value == null) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }

  return str
}

const exportAllReservations = function () {
  const reservations = currentVisibleReservations.value
  if (!reservations.length) return

  const columns = [
    { header: 'Status', value: r => r.status },
    { header: 'Market', value: r => r.market },
    { header: 'Date', value: r => r.date },
    { header: 'Preferred Time', value: r => r.preferredTime },
    { header: 'Confirmed Time', value: r => r.confirmedTime },
    { header: 'Party Size', value: r => r.partySize },
    { header: 'Preferred Restaurant', value: r => r.preferredRestaurant },
    { header: 'Alternate Restaurants', value: r => Array.isArray(r.alternateRestaurants) ? r.alternateRestaurants.join('; ') : r.alternateRestaurants },
    { header: 'Confirmed Restaurant', value: r => r.confirmedRestaurant },
    { header: 'Restaurant Type', value: r => r.restaurantData?.relevantRestaurantStatus },
    { header: 'Restaurant Group', value: r => r.restaurantData?.relevantRestaurantGroup },
    { header: 'Member First Name', value: r => r.owner?.firstName },
    { header: 'Member Last Name', value: r => r.owner?.lastName },
    { header: 'Member Email', value: r => r.owner?.email },
    { header: 'Member Company', value: r => r.owner?.company },
    { header: 'Occasion', value: r => r.occasion },
    { header: 'Notes / Requests', value: r => r.specialRequests },
    { header: 'Min Spend Per Person', value: r => r.minSpendPerPerson },
    { header: 'Flexibility', value: r => r.flexibility },
    { header: 'Submitted At', value: r => r.submittedAt ? moment(r.submittedAt).format('YYYY-MM-DD HH:mm') : '' },
    { header: 'Accepted At', value: r => r.acceptedAt ? moment(r.acceptedAt).format('YYYY-MM-DD HH:mm') : '' },
    { header: 'Confirmed By', value: r => r.confirmedByName },
    { header: 'Reservation Type', value: r => r.reservationType },
  ]

  const headerRow = columns.map(c => escapeCsvField(c.header)).join(',')
  const dataRows = reservations.map(r =>
    columns.map(c => escapeCsvField(c.value(r))).join(','),
  )

  const csvContent = [headerRow, ...dataRows].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'reservations.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// ============ FILTER LOGIC ============

const restaurantOptions = computed(() => {
  if (!restaurantsStore.restaurants?.length) return []

  // Restaurant admins (not platform admins) only see restaurants in their scope.
  const scopedIds = !isPlatformAdmin.value && Array.isArray(accessibleRestaurantIds.value)
    ? new Set(accessibleRestaurantIds.value)
    : null

  const options = restaurantsStore.restaurants
    .filter(restaurant => restaurant?._id && restaurant.name && restaurant.market)
    .filter(restaurant => !scopedIds || scopedIds.has(String(restaurant._id)))
    .map(restaurant => ({
      title: `${restaurant.name} - ${restaurant.market}`,
      value: String(restaurant._id),
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  return [{ title: 'All restaurants', value: null }, ...options]
})

const restaurantGroupOptions = computed(() => {
  if (!restaurantsStore.groups) return []

  return restaurantsStore.groups
})

// Only cities with at least one actual request — not every city that has a
// restaurant on file, and independent of the Time period selector so the
// option list doesn't reactively shrink as the analytics date range changes —
// see loadRequestedCities().
const cityOptions = computed(() => requestedCities.value)

const selectedRestaurant = computed(() => {
  if (!filterRestaurantId.value) return null

  return restaurantsStore.restaurants.find(
    restaurant => String(restaurant._id) === String(filterRestaurantId.value),
  ) || null
})

function normalizeRestaurantId(id) {
  if (!id) return null

  return String(id)
}

function reservationMatchesRestaurantId(reservation, restaurantId) {
  const targetId = normalizeRestaurantId(restaurantId)
  if (!targetId) return false

  const directIds = [
    reservation.confirmedRestaurantId,
    reservation.preferredRestaurantId,
    ...(reservation.alternateRestaurantIds || []),
  ]
    .map(normalizeRestaurantId)
    .filter(Boolean)

  if (directIds.includes(targetId)) return true

  return reservation.restaurantData?.requestedRestaurants?.some(
    restaurant => normalizeRestaurantId(restaurant.id) === targetId,
  ) || false
}

function reservationMatchesLegacyRestaurant(reservation, restaurant) {
  if (!restaurant?.name || !restaurant.market) return false
  if (reservation.market !== restaurant.market) return false

  return reservation.confirmedRestaurant === restaurant.name
    || reservation.preferredRestaurant === restaurant.name
    || reservation.alternateRestaurants?.includes(restaurant.name)
    || reservation.restaurantData?.requestedRestaurants?.some(rest =>
      rest.name === restaurant.name && rest.market === restaurant.market,
    ) || false
}

function applyFilters(reservationsList) {
  let filtered = reservationsList
  if (filterRestaurantType.value && filterRestaurantType.value !== 'All') {
    filtered = filtered.filter(r => {
      if (r.restaurantData?.relevantRestaurantStatus) {
        return r.restaurantData.relevantRestaurantStatus === filterRestaurantType.value
      }
      if (r.restaurantData?.requestedRestaurants) {
        return r.restaurantData.requestedRestaurants.some(
          rest => rest.status === filterRestaurantType.value
        )
      }

      return false
    })
  }
  if (filterRestaurantId.value) {
    filtered = filtered.filter(r =>
      reservationMatchesRestaurantId(r, filterRestaurantId.value)
      || reservationMatchesLegacyRestaurant(r, selectedRestaurant.value)
    )
  }
  if (filterRestaurantGroup.value) {
    filtered = filtered.filter(r =>
      r.restaurantData?.requestedRestaurants?.some(
        rest => rest.group === filterRestaurantGroup.value
      )
      || r.restaurantData?.relevantRestaurantGroup === filterRestaurantGroup.value
    )
  }
  if (filterCity.value) {
    filtered = filtered.filter(r =>
      r.restaurantData?.requestedRestaurants?.some(
        rest => rest.market === filterCity.value
      )
      || r.restaurantData?.relevantRestaurantMarket === filterCity.value
    )
  }

  return filtered
}

function clearFilters() {
  filterRestaurantType.value = 'All'
  filterRestaurantId.value = null
  filterRestaurantGroup.value = null
  filterCity.value = null
}

const hasActiveFilters = computed(() => {
  return filterRestaurantType.value !== 'All'
    || !!filterRestaurantId.value
    || !!filterRestaurantGroup.value
    || !!filterCity.value
})

// ============ IN-PROGRESS RESERVATIONS ============

// RM-400 — a group/private dining inquiry is one record sent to up to 10 restaurants.
// A restaurant team (or the admin looking at one restaurant) sees its OWN entry, titled
// with that restaurant and listing the others it was sent to, before and after the member
// books elsewhere. The admin's "All restaurants" view keeps one combined entry.
function inquiryRestaurantNames(el) {
  const names = []
  if (el.preferredRestaurant) names.push(el.preferredRestaurant)
  if (Array.isArray(el.alternateRestaurants)) names.push(...el.alternateRestaurants.filter(Boolean))
  
  return names.sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase()))
}
function inquiryEntriesFor(el, { status = 'Submitted' } = {}) {
  const split = !isPlatformAdmin.value || !!filterRestaurantId.value
  if (!split) return [el]
  const names = inquiryRestaurantNames(el)
  const scope = !isPlatformAdmin.value && Array.isArray(accessibleRestaurantIds.value)
    ? new Set(accessibleRestaurantIds.value.map(String))
    : null
  
  return names
    .filter(name => {
      if (!scope) return true
      const info = el.restaurantData?.requestedRestaurants?.find(r => r.name === name)
      
      return !info || scope.has(String(info.id))
    })
    .map(name => ({
      ...el,
      status,
      _displayRestaurantName: name,
      _alsoSentTo: names.filter(n => n !== name),
      _uniqueKey: `${el._id}-${name}`,
    }))
}

const reservationsIncoming = computed(() => {
  if (!inReviewReservations.value) return []

  const returnArray = []

  inReviewReservations.value.forEach(el => {
    if (el.reservationType === 'private' && (el.status === 'Submitted' || isApproveRow(el))) {
      // "Approve?" is RM's; to a restaurant the inquiry reads Submitted (the API masks it too).
      // RM works those rows under In Progress → Approve?, so its own unfiltered view lists them once.
      if (isApproveRow(el) && !incomingKeepsApprove({ isPlatformAdmin: isPlatformAdmin.value, restaurantFilter: filterRestaurantId.value })) return
      returnArray.push(...inquiryEntriesFor(el, { status: 'Submitted' }))
    } else if (el.status === 'Submitted') {
      returnArray.push(el)
    } else if (el.status === 'Accepted' && el.confirmedRestaurantId) {
      // Collect all requested restaurant IDs
      const allIds = []
      if (el.preferredRestaurantId) allIds.push(String(el.preferredRestaurantId))
      if (el.alternateRestaurantIds?.length) {
        el.alternateRestaurantIds.forEach(id => allIds.push(String(id)))
      }

      // Remove the confirmed restaurant
      const confirmedId = String(el.confirmedRestaurantId)
      const remainingIds = allIds.filter(id => id !== confirmedId)

      // Create an entry for each non-confirmed restaurant
      remainingIds.forEach(restaurantId => {
        const restaurantInfo = el.restaurantData?.requestedRestaurants?.find(
          r => String(r.id) === restaurantId
        )
        returnArray.push({
          ...el,
          status: 'Submitted',
          confirmedRestaurant: restaurantInfo?.name || null,
          confirmedRestaurantId: null,
          confirmedByName: null,
          confirmedTime: null,
          confirmedMessageToRm: null,
          confirmedMessageToMember: null,
          acceptedAt: null,
          _displayRestaurantId: restaurantId,
          _displayRestaurantName: restaurantInfo?.name,
          _uniqueKey: `${el._id}-${restaurantId}`,
        })
      })
    }
  })

  // Newest event first, so the upcoming inquiries lead and the historical ones (every past
  // inquiry reads Submitted since 10/21) sit below them - Kapil's 10/21 test: a 12/11 inquiry
  // was buried among dozens of 2023-2025 rows ordered by status change.
  returnArray.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(b.lastStatusChangeAt || '').localeCompare(String(a.lastStatusChangeAt || '')))

  return applyFilters(returnArray)
})

const reservationsAwaitingResponse = computed(() => {
  if (!inReviewReservations.value) return []
  var returnArray = inReviewReservations.value.filter(function (el) {
    return el.status == 'Awaiting Response from Restaurant'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) {
      return -1
    }
    if (a.sortDateTime > b.sortDateTime) {
      return 1
    }

    return 0
  })

  return applyFilters(returnArray)
})

const reservationsInReview = computed(() => {
  if (!inReviewReservations.value) return []
  var returnArray = inReviewReservations.value.filter(function (el) {
    return el.status == 'In Review'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) {
      return -1
    }
    if (a.sortDateTime > b.sortDateTime) {
      return 1
    }

    return 0
  })

  return applyFilters(returnArray)
})


// RM-359: RM's own queue — submitted, waiting for RM approval, not sent to the
// restaurant (platform admins only: a restaurant's list never includes these).
// Decided on /admin-approvals.
const reservationsApprovalNeeded = computed(() => {
  if (!inReviewReservations.value) return []
  const returnArray = inReviewReservations.value.filter(el => el.status == 'Pending RM Review')
  returnArray.sort((a, b) => (a.submittedAt || '') < (b.submittedAt || '') ? -1 : 1)

  return applyFilters(returnArray)
})

const reservationsMemberAction = computed(() => {
  if (!inReviewReservations.value) return []
  var returnArray = inReviewReservations.value.filter(function (el) {
    return el.status == 'Member Action'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) {
      return -1
    }
    if (a.sortDateTime > b.sortDateTime) {
      return 1
    }

    return 0
  })

  return applyFilters(returnArray)
})


// RM-400 — inquiries the member marked as booked, waiting for RM's approval:
// In Progress → Approve? (RM's work to do; Kapil, 10/22). The rule: utils/adminReservationBuckets.
const reservationsApprove = computed(() => applyFilters(approveRows(inReviewReservations.value)))

const reservationsUpcoming = computed(() => {
  if (!inReviewReservations.value) return []
  var returnArray = inReviewReservations.value.filter(function (el) {
    return (el.status == "Accepted" && moment(el.sortDateTime).isAfter(moment()))
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) {
      return -1
    }
    if (a.sortDateTime > b.sortDateTime) {
      return 1
    }

    return 0
  })

  return applyFilters(returnArray)
})

// ============ COMPLETE RESERVATIONS ============

const earliestDate = computed(() => {
  switch (period.value) {
  case 'Custom': return customStartDate.value || '2000-01-01'
  case 'Past Month': return moment().subtract(1, 'months').format('YYYY-MM-DD')
  case 'Past 3 Months': return moment().subtract(3, 'months').format('YYYY-MM-DD')
  case 'Past Year': return moment().subtract(1, 'years').format('YYYY-MM-DD')
  case 'YTD': return `${moment().year() - 1}-12-31`
  case 'Start of Last Year': return `${moment().year() - 2}-12-31`
  case '2025': return '2024-12-31'
  case '2024': return '2023-12-31'
  case '2023': return '2022-12-31'
  case '2022': return '2021-12-31'
  case '2021': return '2020-12-31'
  case '2020': return '2019-12-31'
  default: return '2000-01-01'
  }
})

const latestDate = computed(() => {
  if (period.value === 'Custom') {
    return customEndDate.value || null
  }
  const match = period.value.match(/^(20\d{2})$/)
  if (match) {
    return `${parseInt(match[1]) + 1}-01-01`
  }

  return null
})

const reservationsExperienced = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return ((el.status == "Accepted" || el.status == "Experienced") && moment(el.sortDateTime).isBefore(moment()))
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})

const reservationsNotAvailable = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return el.status == 'Not Available'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})


const reservationsNoResponse = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return el.status == 'No Response'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})


const reservationsDeclined = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return el.status == 'Declined'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})

const reservationsCanceled = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return el.status == 'Canceled'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})

const reservationsRescinded = computed(() => {
  if (!completeReservations.value) return []
  var returnArray = completeReservations.value.filter(function (el) {
    return el.status == 'Rescinded'
  })
  returnArray.sort(function (a, b) {
    if (a.sortDateTime < b.sortDateTime) return 1
    if (a.sortDateTime > b.sortDateTime) return -1

    return 0
  })

  return applyFilters(returnArray)
})

function computeSpendMetrics(reservations) {
  const values = reservations
    .map(r => r.minSpendPerPerson)
    .filter(v => v != null && v > 0)
  if (values.length === 0) return { avgSpendPerPerson: 'N/A', medianSpendPerPerson: 'N/A' }
  const avg = Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const median = Math.round(sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2)

  return { avgSpendPerPerson: avg, medianSpendPerPerson: median }
}

function getResponseTimeHours(responseDate, submittedAt) {
  if (!responseDate || !submittedAt) return null
  const responseMs = moment(responseDate).diff(moment(submittedAt))

  return responseMs >= 0 ? responseMs / 1000 / 60 / 60 : null
}

function isAnalyticsReservation(reservation) {
  return reservation?.reservationType !== 'private'
}

function filterAnalyticsReservations(reservations) {
  return reservations.filter(isAnalyticsReservation)
}

function computeLeadTimeMetrics(reservations) {
  const leadTimesDays = []
  for (const r of reservations) {
    if (r.submittedAt && r.sortDateTime) {
      const leadMs = moment(r.sortDateTime).diff(moment(r.submittedAt))
      if (leadMs > 0) leadTimesDays.push(leadMs / 1000 / 60 / 60 / 24)
    }
  }
  if (leadTimesDays.length === 0) {
    return {
      avgLeadTimeDays: 'N/A',
      medianLeadTimeDays: 'N/A',
      shortestLeadTimeDays: 'N/A',
      longestLeadTimeDays: 'N/A',
    }
  }
  const formatDays = v => (v > 0 && v < 0.1 ? '0.1' : v.toFixed(1))
  const avgRaw = leadTimesDays.reduce((s, v) => s + v, 0) / leadTimesDays.length
  const sorted = [...leadTimesDays].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const medianRaw = sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2

  return {
    avgLeadTimeDays: formatDays(avgRaw),
    medianLeadTimeDays: formatDays(medianRaw),
    shortestLeadTimeDays: formatDays(sorted[0]),
    longestLeadTimeDays: formatDays(sorted[sorted.length - 1]),
  }
}

function computeResponseMetrics(acceptedReservations, declinedReservations) {
  const responseTimesHours = [
    ...acceptedReservations.map(r => getResponseTimeHours(r.acceptedAt, r.submittedAt)),
    ...declinedReservations.map(r => getResponseTimeHours(r.declinedAt || r.lastStatusChangeAt, r.submittedAt)),
  ].filter(v => v != null)

  if (responseTimesHours.length === 0) {
    return {
      avgResponseTimeHours: 'N/A',
      medianResponseTimeHours: 'N/A',
      fastestResponseTimeMinutes: 'N/A',
      slowestResponseTimeMinutes: 'N/A',
    }
  }

  const avg = (responseTimesHours.reduce((sum, value) => sum + value, 0) / responseTimesHours.length).toFixed(1)
  const sorted = [...responseTimesHours].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const median = (sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2).toFixed(1)

  const formatHours = v => (v > 0 && v < 0.1 ? '0.1' : v.toFixed(1))

  return {
    avgResponseTimeHours: avg,
    medianResponseTimeHours: median,
    fastestResponseTimeHours: formatHours(sorted[0]),
    slowestResponseTimeHours: formatHours(sorted[sorted.length - 1]),
  }
}

// Computed analytics that reacts to filters
const displayAnalytics = computed(() => {
  // When no filters active, use backend analytics supplemented with upcoming
  if (!hasActiveFilters.value) {
    const base = completedAnalytics.value
    if (!base) return null
    const analyticsUpcoming = filterAnalyticsReservations(reservationsUpcoming.value)
    const analyticsExperienced = filterAnalyticsReservations(reservationsExperienced.value)
    const analyticsCanceled = filterAnalyticsReservations(reservationsCanceled.value)
    const analyticsNotAvailable = filterAnalyticsReservations(reservationsNotAvailable.value)
    const analyticsDeclined = filterAnalyticsReservations(reservationsDeclined.value)
    const upcomingCount = analyticsUpcoming.length
    const newAcceptedCount = (base.acceptedCount || 0) + upcomingCount
    const newTotalRequests = (base.totalRequests || 0) + upcomingCount
    const resolvedTotal = newAcceptedCount + (base.notAvailableCount || 0)

    // Only recompute frontend metrics when reservation arrays have loaded —
    // otherwise fall back to backend values so the stat cards don't get
    // clobbered with 'N/A' while completeReservations is still fetching.
    const spendSource = [...analyticsExperienced, ...analyticsCanceled, ...analyticsUpcoming]
    const responseAccepted = analyticsExperienced
    const responseDenied = [...analyticsNotAvailable, ...analyticsDeclined]

    const spendMetrics = spendSource.length > 0
      ? computeSpendMetrics(spendSource)
      : {
        avgSpendPerPerson: base.avgSpendPerPerson ?? 'N/A',
        medianSpendPerPerson: base.medianSpendPerPerson ?? 'N/A',
      }
    const responseMetrics = (responseAccepted.length + responseDenied.length) > 0
      ? computeResponseMetrics(responseAccepted, responseDenied)
      : {
        avgResponseTimeHours: base.avgResponseTimeHours ?? 'N/A',
        medianResponseTimeHours: base.medianResponseTimeHours ?? 'N/A',
        fastestResponseTimeHours: base.fastestResponseTimeHours ?? 'N/A',
        slowestResponseTimeHours: base.slowestResponseTimeHours ?? 'N/A',
      }
    const leadTimeMetrics = spendSource.length > 0
      ? computeLeadTimeMetrics(spendSource)
      : {
        avgLeadTimeDays: base.avgLeadTimeDays ?? 'N/A',
        medianLeadTimeDays: base.medianLeadTimeDays ?? 'N/A',
        shortestLeadTimeDays: base.shortestLeadTimeDays ?? 'N/A',
        longestLeadTimeDays: base.longestLeadTimeDays ?? 'N/A',
      }

    return {
      ...base,
      upcomingCount,
      acceptedCount: newAcceptedCount,
      totalRequests: newTotalRequests,
      acceptanceRate: resolvedTotal > 0
        ? ((newAcceptedCount / resolvedTotal) * 100).toFixed(1) : 'N/A',
      ...spendMetrics,
      ...responseMetrics,
      ...leadTimeMetrics,
    }
  }

  // Compute from filtered frontend data
  const experienced = filterAnalyticsReservations(reservationsExperienced.value)
  const canceled = filterAnalyticsReservations(reservationsCanceled.value)
  const notAvailableRaw = filterAnalyticsReservations(reservationsNotAvailable.value)
  const noResponse = filterAnalyticsReservations(reservationsNoResponse.value)
  const declined = filterAnalyticsReservations(reservationsDeclined.value)
  const rescinded = filterAnalyticsReservations(reservationsRescinded.value)
  const upcoming = filterAnalyticsReservations(reservationsUpcoming.value)
  const acceptedCount = experienced.length + canceled.length + upcoming.length
  const notAvailableCount = notAvailableRaw.length + noResponse.length + declined.length
  const resolvedTotal = acceptedCount + notAvailableCount
  const totalRequests = resolvedTotal + rescinded.length

  // Type breakdown
  const preferred = { acceptedCount: 0, notAvailableCount: 0 }
  const memberFavorite = { acceptedCount: 0, notAvailableCount: 0 }

  for (const r of [...experienced, ...canceled, ...upcoming]) {
    const st = r.restaurantData?.relevantRestaurantStatus
    if (st === 'Preferred') preferred.acceptedCount++
    else if (st === 'Member Favorite') memberFavorite.acceptedCount++
  }
  for (const r of [...notAvailableRaw, ...noResponse, ...declined]) {
    const st = r.restaurantData?.relevantRestaurantStatus
    if (st === 'Preferred') preferred.notAvailableCount++
    else if (st === 'Member Favorite') memberFavorite.notAvailableCount++
  }

  const acceptanceRate = resolvedTotal > 0
    ? ((acceptedCount / resolvedTotal) * 100).toFixed(1) : 'N/A'
  const cancellationRate = acceptedCount > 0
    ? ((canceled.length / acceptedCount) * 100).toFixed(1) : 'N/A'

  const prefTotal = preferred.acceptedCount + preferred.notAvailableCount
  preferred.acceptanceRate = prefTotal > 0
    ? ((preferred.acceptedCount / prefTotal) * 100).toFixed(1) : 'N/A'
  const mfTotal = memberFavorite.acceptedCount + memberFavorite.notAvailableCount
  memberFavorite.acceptanceRate = mfTotal > 0
    ? ((memberFavorite.acceptedCount / mfTotal) * 100).toFixed(1) : 'N/A'
  const responseMetrics = computeResponseMetrics(experienced, [...notAvailableRaw, ...declined])

  const leadTimeMetrics = computeLeadTimeMetrics([...experienced, ...canceled, ...upcoming])

  // Total guaranteed spend
  let totalGuaranteedSpend = 0
  for (const r of [...experienced, ...canceled, ...upcoming]) {
    if (r.minSpendPerPerson && r.partySize) {
      totalGuaranteedSpend += r.minSpendPerPerson * r.partySize
    }
  }

  const spendMetrics = computeSpendMetrics([...experienced, ...canceled, ...upcoming])

  return {
    totalRequests,
    experiencedCount: experienced.length,
    upcomingCount: upcoming.length,
    canceledCount: canceled.length,
    acceptedCount,
    notAvailableRawCount: notAvailableRaw.length,
    noResponseCount: noResponse.length,
    declinedCount: declined.length,
    notAvailableCount,
    rescindedCount: rescinded.length,
    acceptanceRate,
    cancellationRate,
    ...leadTimeMetrics,
    totalGuaranteedSpend,
    ...spendMetrics,
    ...responseMetrics,
    preferred,
    memberFavorite,
  }
})

// Helper for refresh button
const handleRefresh = () => {
  if (parentTab.value === 'stats') {
    ensureCompletedDataLoaded()
  } else if (parentTab.value === 'completed') {
    reservationsStore.getAllCompleteReservations(earliestDate.value, latestDate.value)
  } else {
    reservationsStore.getInReviewReservations()
  }
}

// Comms panel: admin can pick a reservation ID to view its comms history.
const commsReservationId = ref('')

const reservationOptions = computed(() => {
  return (currentVisibleReservations.value || []).map(r => {
    const restaurant = r.confirmedRestaurant || r.preferredRestaurant || '—'
    const date = r.date ? moment(r.date).format('M/D/YY') : 'no date'
    const member = r.owner ? `${r.owner.firstName || ''} ${r.owner.lastName || ''}`.trim() : ''
    const label = `${restaurant} · ${date}${member ? ' · ' + member : ''} (${r.status})`

    return { label, value: r._id }
  })
})

// ============ UI CONFIG ============

const restaurantTypeOptions = ['All', 'Preferred', 'Member Favorite']

const periodOptions = [
  { title: 'All time', value: 'All' },
  { title: 'Past month', value: 'Past Month' },
  { title: 'Past 3 months', value: 'Past 3 Months' },
  { title: 'Past year', value: 'Past Year' },
  { title: 'Year to date', value: 'YTD' },
  { title: 'Since start of last year', value: 'Start of Last Year' },
  { title: '2025', value: '2025' },
  { title: '2024', value: '2024' },
  { title: '2023', value: '2023' },
  { title: '2022', value: '2022' },
  { title: '2021', value: '2021' },
  { title: '2020', value: '2020' },
  { title: 'Custom range…', value: 'Custom' },
]

const inProgressSubTabs = computed(() => [
  ...(isPlatformAdmin.value ? [{ value: 'approval needed', label: 'Pending RM Review', count: reservationsApprovalNeeded.value.length, emphasis: reservationsApprovalNeeded.value.length > 0 }] : []),
  { value: 'awaiting response', label: 'Awaiting Response', count: reservationsAwaitingResponse.value.length, emphasis: true },
  // Member-reported bookings waiting for RM's approval: RM's work to do, so it sits here, next to the
  // restaurants to chase, not under Completed (Kapil, 10/22). RM only - a restaurant never sees the word.
  ...(isPlatformAdmin.value ? [{ value: 'Approve?', label: 'Approve?', count: reservationsApprove.value.length, emphasis: reservationsApprove.value.length > 0 }] : []),
  { value: 'in review', label: 'In Review', count: reservationsInReview.value.length },
  { value: 'member action', label: 'Member Action', count: reservationsMemberAction.value.length },
  { value: 'upcoming', label: 'Upcoming', count: reservationsUpcoming.value.length },
])

// The In Progress badge counts RM's work to do: restaurants to chase and, for RM, bookings to approve.
const approveCount = computed(() => (isPlatformAdmin.value ? reservationsApprove.value.length : 0))
const inProgressWorkCount = computed(() => reservationsAwaitingResponse.value.length + approveCount.value)
const inProgressWorkLabel = computed(() => [
  reservationsAwaitingResponse.value.length > 0 ? `${reservationsAwaitingResponse.value.length} awaiting response` : '',
  approveCount.value > 0 ? `${approveCount.value} to approve` : '',
].filter(Boolean).join(', '))

const completedSubTabs = computed(() => [
  { value: 'Experienced', label: 'Experienced', count: reservationsExperienced.value.length },
  { value: 'Upcoming', label: 'Upcoming', count: reservationsUpcoming.value.length },
  { value: 'Not Available', label: 'Not Available', count: reservationsNotAvailable.value.length },
  { value: 'No Response', label: 'No Response', count: reservationsNoResponse.value.length },
  { value: 'Declined', label: 'Declined', count: reservationsDeclined.value.length },
  { value: 'Rescinded', label: 'Rescinded', count: reservationsRescinded.value.length },
  { value: 'Canceled', label: 'Canceled', count: reservationsCanceled.value.length },
  { value: 'Submitted', label: 'Submitted', count: reservationsIncoming.value.length },
])

const emptyMessages = {
  'Approve?': 'No group/private dining bookings are waiting for approval.',
  'awaiting response': 'No requests are awaiting response.',
  'in review': 'No requests are currently in review.',
  'member action': 'No requests need member action.',
  'approval needed': 'Nothing is pending RM review. Decide on requests at /admin-approvals.',
  'upcoming': 'No upcoming reservations.',
  'Experienced': 'No experienced reservations in this period.',
  'Upcoming': 'No upcoming reservations.',
  'Not Available': 'No "not available" reservations in this period.',
  'No Response': 'No unresponsive reservations in this period.',
  'Declined': 'No declined reservations in this period.',
  'Rescinded': 'No rescinded reservations in this period.',
  'Canceled': 'No canceled reservations in this period.',
  'Submitted': 'No submitted requests in this period.',
}

const currentEmptyMessage = computed(() => {
  const key = parentTab.value === 'in-progress' ? inProgressSubTab.value : completedSubTab.value

  return emptyMessages[key] || 'Nothing to show yet.'
})

const activeSubTabs = computed(() =>
  parentTab.value === 'in-progress' ? inProgressSubTabs.value : completedSubTabs.value,
)

const activeSubTab = computed({
  get() {
    return parentTab.value === 'in-progress' ? inProgressSubTab.value : completedSubTab.value
  },
  set(value) {
    if (parentTab.value === 'in-progress') inProgressSubTab.value = value
    else completedSubTab.value = value
  },
})
</script>

<template>
  <div class="rr-page">
    <template v-if="myUser">
      <!-- Page header -->
      <header class="rr-header">
        <div class="rr-header__title-row">
          <h1 class="rr-header__title">
            Bookings
          </h1>
          <div class="rr-header__actions">
            <VTooltip
              v-if="canExportCsv && parentTab !== 'stats'"
              text="Download visible reservations to CSV"
              location="bottom"
            >
              <template #activator="{ props: tooltipProps }">
                <VBtn
                  v-bind="tooltipProps"
                  icon="mdi-file-export-outline"
                  variant="text"
                  size="small"
                  color="medium-emphasis"
                  class="rr-header__icon-btn"
                  @click="exportAllReservations"
                />
              </template>
            </VTooltip>
            <VBtn
              icon="mdi-refresh"
              variant="text"
              size="small"
              color="medium-emphasis"
              class="rr-header__icon-btn"
              :loading="fetching"
              @click="handleRefresh"
            />
          </div>
        </div>
      </header>

      <!-- Main card -->
      <VCard
        variant="flat"
        border
        class="rr-card"
      >
        <!-- Parent tabs -->
        <VTabs
          v-model="parentTab"
          color="primary-dark"
          grow
          hide-slider
          height="72"
          class="rr-parent-tabs"
        >
          <VTab value="in-progress">
            <span class="rr-parent-tab-label">
              In Progress
              <span
                v-if="inProgressWorkCount > 0"
                class="rr-parent-tab-badge"
                :aria-label="inProgressWorkLabel"
              >
                {{ inProgressWorkCount }}
              </span>
            </span>
          </VTab>
          <VTab value="completed">
            Completed
          </VTab>
          <VTab value="stats">
            Stats
          </VTab>
        </VTabs>

        <!-- Filters -->
        <div class="rr-filters">
          <div
            v-if="isPlatformAdmin"
            class="rr-filters__chips"
          >
            <button
              v-for="option in restaurantTypeOptions"
              :key="option"
              type="button"
              class="rr-chip"
              :class="{ 'rr-chip--active': filterRestaurantType === option }"
              @click="filterRestaurantType = option"
            >
              {{ option }}
            </button>
          </div>
          <div class="rr-filters__inputs">
            <VAutocomplete
              v-model="filterRestaurantId"
              :items="restaurantOptions"
              label="Restaurant"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              class="rr-filters__input"
            />
            <VAutocomplete
              v-if="isPlatformAdmin"
              v-model="filterRestaurantGroup"
              :items="restaurantGroupOptions"
              label="Restaurant group"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              class="rr-filters__input"
            />
            <VAutocomplete
              v-if="isPlatformAdmin"
              v-model="filterCity"
              :items="cityOptions"
              label="City"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              class="rr-filters__input"
            />
            <VBtn
              v-if="hasActiveFilters"
              variant="text"
              size="small"
              color="medium-emphasis"
              class="rr-filters__clear"
              @click="clearFilters"
            >
              Clear filters
            </VBtn>
          </div>
        </div>

        <VDivider />

        <!-- Loading -->
        <div
          v-if="fetching"
          class="rr-loading"
        >
          <VProgressCircular
            indeterminate
            color="primary-dark"
            size="40"
          />
        </div>

        <!-- Content windows -->
        <div v-show="!fetching">
          <!-- Period selector (Stats and Completed) -->
          <div
            v-if="parentTab === 'stats' || parentTab === 'completed'"
            class="rr-period"
          >
            <VSelect
              v-model="period"
              :items="periodOptions"
              item-title="title"
              item-value="value"
              label="Time period"
              density="compact"
              variant="outlined"
              hide-details
              class="rr-period__select"
            />
            <VTextField
              v-if="period === 'Custom'"
              v-model="customStartDate"
              label="Start date"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
              class="rr-period__date"
            />
            <VTextField
              v-if="period === 'Custom'"
              v-model="customEndDate"
              label="End date"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
              class="rr-period__date"
            />
          </div>

          <!-- Analytics (Stats only) -->
          <VProgressLinear
            v-if="parentTab === 'stats' && ((analyticsLoading && !hasActiveFilters) || !completeReservationsReady)"
            indeterminate
            color="primary-dark"
            class="rr-analytics__loader"
          />

          <section
            v-if="parentTab === 'stats' && displayAnalytics && completeReservationsReady && !(analyticsLoading && !hasActiveFilters)"
            class="rr-analytics"
          >
            <!-- Hero: 3 headline stats -->
            <div class="rr-hero-stats">
              <div class="rr-hero-stat">
                <div class="rr-hero-stat__label">
                  Total requests
                </div>
                <div class="rr-hero-stat__value">
                  {{ displayAnalytics.totalRequests }}
                </div>
              </div>
              <div class="rr-hero-stat">
                <div class="rr-hero-stat__label">
                  Acceptance rate
                </div>
                <div class="rr-hero-stat__value rr-hero-stat__value--accent">
                  {{ displayAnalytics.acceptanceRate }}<span
                    v-if="displayAnalytics.acceptanceRate !== 'N/A'"
                    class="rr-hero-stat__unit"
                  >%</span>
                </div>
              </div>
              <div class="rr-hero-stat">
                <div class="rr-hero-stat__label">
                  Total guaranteed spend
                </div>
                <div class="rr-hero-stat__value">
                  <span class="rr-hero-stat__unit">$</span>{{ numberWithCommas(displayAnalytics.totalGuaranteedSpend) }}
                </div>
              </div>
            </div>

            <!-- Outcome breakdowns -->
            <div class="rr-outcomes">
              <div class="rr-outcome rr-outcome--positive">
                <div class="rr-outcome__value">
                  {{ displayAnalytics.acceptedCount }}
                </div>
                <div class="rr-outcome__label">
                  Accepted
                </div>
                <div class="rr-outcome__breakdown">
                  <span>{{ displayAnalytics.experiencedCount }} experienced</span>
                  <span>{{ displayAnalytics.upcomingCount || 0 }} upcoming</span>
                  <span>{{ displayAnalytics.canceledCount }} canceled</span>
                </div>
              </div>
              <div class="rr-outcome rr-outcome--negative">
                <div class="rr-outcome__value">
                  {{ displayAnalytics.notAvailableCount }}
                </div>
                <div class="rr-outcome__label">
                  Not available
                </div>
                <div class="rr-outcome__breakdown">
                  <span>{{ displayAnalytics.notAvailableRawCount }} n/a</span>
                  <span>{{ displayAnalytics.noResponseCount }} no response</span>
                  <span>{{ displayAnalytics.declinedCount }} declined</span>
                </div>
              </div>
            </div>

            <!-- Secondary metrics -->
            <div class="rr-metrics">
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Avg response time
                </div>
                <div class="rr-metric__value">
                  {{ displayAnalytics.avgResponseTimeHours }}<span
                    v-if="displayAnalytics.avgResponseTimeHours !== 'N/A'"
                    class="rr-metric__unit"
                  >h</span>
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Median response time
                </div>
                <div class="rr-metric__value">
                  {{ displayAnalytics.medianResponseTimeHours }}<span
                    v-if="displayAnalytics.medianResponseTimeHours !== 'N/A'"
                    class="rr-metric__unit"
                  >h</span>
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Response range
                </div>
                <div class="rr-metric__value rr-metric__value--sm">
                  {{ displayAnalytics.fastestResponseTimeHours !== 'N/A'
                    ? `${displayAnalytics.fastestResponseTimeHours}h – ${displayAnalytics.slowestResponseTimeHours}h`
                    : 'N/A' }}
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Avg advance booking
                </div>
                <div class="rr-metric__value">
                  {{ displayAnalytics.avgLeadTimeDays }}<span
                    v-if="displayAnalytics.avgLeadTimeDays !== 'N/A'"
                    class="rr-metric__unit"
                  >d</span>
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Median advance booking
                </div>
                <div class="rr-metric__value">
                  {{ displayAnalytics.medianLeadTimeDays }}<span
                    v-if="displayAnalytics.medianLeadTimeDays !== 'N/A'"
                    class="rr-metric__unit"
                  >d</span>
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Advance booking range
                </div>
                <div class="rr-metric__value rr-metric__value--sm">
                  {{ displayAnalytics.shortestLeadTimeDays !== 'N/A'
                    ? `${displayAnalytics.shortestLeadTimeDays}d – ${displayAnalytics.longestLeadTimeDays}d`
                    : 'N/A' }}
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Avg spend / person
                </div>
                <div class="rr-metric__value">
                  <template v-if="displayAnalytics.avgSpendPerPerson !== 'N/A'">
                    <span class="rr-metric__unit">$</span>{{ numberWithCommas(displayAnalytics.avgSpendPerPerson) }}
                  </template>
                  <template v-else>
                    N/A
                  </template>
                </div>
              </div>
              <div class="rr-metric">
                <div class="rr-metric__label">
                  Median spend / person
                </div>
                <div class="rr-metric__value">
                  <template v-if="displayAnalytics.medianSpendPerPerson !== 'N/A'">
                    <span class="rr-metric__unit">$</span>{{ numberWithCommas(displayAnalytics.medianSpendPerPerson) }}
                  </template>
                  <template v-else>
                    N/A
                  </template>
                </div>
              </div>
              <div
                v-if="isPlatformAdmin"
                class="rr-metric"
              >
                <div class="rr-metric__label">
                  Cancellation rate
                </div>
                <div class="rr-metric__value">
                  <strong>{{ displayAnalytics.cancellationRate }}<template
                    v-if="displayAnalytics.cancellationRate !== 'N/A'"
                  >%</template></strong>
                </div>
              </div>
            </div>
          </section>

          <!-- Sub-tab pill strip -->
          <div
            v-if="parentTab !== 'stats'"
            class="rr-subtabs"
          >
            <div class="rr-subtabs__scroll">
              <button
                v-for="sub in activeSubTabs"
                :key="sub.value"
                type="button"
                class="rr-subtab"
                :class="{
                  'rr-subtab--active': activeSubTab === sub.value,
                  'rr-subtab--emphasis': sub.emphasis && sub.count > 0 && activeSubTab !== sub.value,
                }"
                @click="activeSubTab = sub.value"
              >
                <span class="rr-subtab__label">{{ sub.label }}</span>
                <span class="rr-subtab__count">{{ sub.count }}</span>
              </button>
            </div>
          </div>

          <!-- Reservation list -->
          <div
            v-if="parentTab !== 'stats'"
            class="rr-list"
          >
            <template v-if="currentVisibleReservations.length > 0">
              <ReservationCardAdmin
                v-for="reservation in currentVisibleReservations"
                :key="reservation._uniqueKey || reservation._id"
                :reservation="reservation"
                :show-response-links="showResponseLinks"
                :fan-data="reservation.owner?._id ? fanDataMap[String(reservation.owner._id)] : null"
                :batched="true"
                class="rr-list__item"
              />
            </template>
            <div
              v-else
              class="rr-empty"
            >
              <VIcon
                size="44"
                color="medium-emphasis"
              >
                mdi-calendar-blank-outline
              </VIcon>
              <div class="rr-empty__title">
                Nothing here
              </div>
              <div class="rr-empty__sub">
                {{ currentEmptyMessage }}
              </div>
            </div>
          </div>
        </div>
      </VCard>

      <!-- Comms history panel (admin only): pick a reservation from the current view. -->
      <VCard
        v-if="myUser?.userLevel === 'admin'"
        class="mt-4 pa-4"
      >
        <div class="text-subtitle-1 mb-3">
          Comms History — Reservation
        </div>
        <VSelect
          v-model="commsReservationId"
          :items="reservationOptions"
          item-title="label"
          item-value="value"
          label="Select a reservation from the current view"
          density="compact"
          clearable
          hide-details
          class="mb-3"
          data-testid="comms-reservation-select"
        />
        <VTextField
          v-model="commsReservationId"
          label="Or paste a reservation ID"
          placeholder="Paste reservation _id"
          density="compact"
          clearable
          hide-details
          class="mb-3"
        />
        <CommsHistoryPanel
          v-if="commsReservationId"
          :reservation-id="commsReservationId"
        />
        <div
          v-else
          class="text-caption text-medium-emphasis"
        >
          Select or paste a reservation ID to load its comms.
        </div>
      </VCard>
    </template>
  </div>
</template>

<style lang="scss" scoped>
/* ─── Page shell ───────────────────────────────────────────────────────── */

.rr-page {
  max-width: 1120px;
  margin: 0 auto;
  padding: var(--ds-space-4) var(--ds-space-3) var(--ds-space-16);
}

@media (min-width: 640px) {
  .rr-page {
    padding: var(--ds-space-6) var(--ds-space-5) var(--ds-space-16);
  }
}

/* ─── Back link ────────────────────────────────────────────────────────── */

.rr-back {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-1);
  padding: var(--ds-space-2) var(--ds-space-3) var(--ds-space-2) var(--ds-space-2);
  margin-bottom: var(--ds-space-3);
  border-radius: 999px;
  font-size: var(--ds-text-sm);
  font-weight: 500;
  color: var(--ds-text-secondary);
  text-decoration: none;
  transition: background-color 150ms ease, color 150ms ease;
}

.rr-back:hover,
.rr-back:focus-visible {
  background-color: rgba(0, 0, 0, 0.04);
  color: var(--ds-text-strong);
}

/* ─── Header ───────────────────────────────────────────────────────────── */

.rr-header {
  margin-bottom: var(--ds-space-4);
}

.rr-header__title-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--ds-space-3);
}

.rr-header__title {
  grid-column: 2;
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-2xl);
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: var(--ds-tracking-tight);
  margin: 0;
  line-height: 1.15;
  text-align: center;
}

@media (min-width: 640px) {
  .rr-header__title {
    font-size: var(--ds-text-3xl);
  }
}

.rr-header__actions {
  grid-column: 3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--ds-space-1);
}

.rr-header__icon-btn {
  border-radius: 999px;
}

/* ─── Card ─────────────────────────────────────────────────────────────── */

.rr-card {
  border-radius: var(--ds-radius-lg, 16px) !important;
  background: var(--ds-surface-card);
  overflow: hidden;
  border-color: var(--ds-border-subtle) !important;
}

/* ─── Parent tabs ──────────────────────────────────────────────────────── */

.rr-parent-tabs {
  border-bottom: 1px solid var(--ds-border-subtle);
  padding: 0 var(--ds-space-2);
  gap: var(--ds-space-2);
}

.rr-parent-tabs :deep(.v-slide-group__container),
.rr-parent-tabs :deep(.v-slide-group__content) {
  align-items: center;
}

.rr-parent-tabs :deep(.v-tab) {
  font-weight: 600;
  font-size: var(--ds-text-base);
  letter-spacing: 0;
  text-transform: none;
  height: 44px;
  min-height: 0;
  align-self: center;
  margin: 0 var(--ds-space-1);
  border-radius: 10px;
  color: var(--ds-text-secondary);
  opacity: 1;
  transition: background-color 180ms ease, color 180ms ease, box-shadow 180ms ease;
}

.rr-parent-tabs :deep(.v-tab:hover:not(.v-tab--selected)) {
  background: var(--ds-brand-primary-light);
  color: var(--ds-brand-primary-darker);
}

.rr-parent-tabs :deep(.v-tab--selected) {
  background: var(--ds-brand-primary-dark);
  color: var(--ds-text-on-dark) !important;
  font-weight: 700;
  box-shadow: 0 4px 12px -4px rgba(63, 153, 143, 0.45);
}

.rr-parent-tabs :deep(.v-tab--selected:hover) {
  background: var(--ds-brand-primary-darker);
}

/* Vuetify paints a translucent overlay on hover/focus that washes out the
   filled selected state — neutralize it so the fill stays solid. */
.rr-parent-tabs :deep(.v-tab--selected .v-tab__overlay) {
  opacity: 0 !important;
}

.rr-parent-tab-label {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-2);
}

.rr-parent-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  background: var(--ds-brand-primary-dark);
  color: var(--ds-text-on-dark);
  border-radius: 999px;
  font-size: var(--ds-text-xs);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 0 0 3px var(--ds-brand-primary-light);
  animation: rr-badge-pulse 2.4s ease-in-out infinite;
}

/* Inverted badge when its tab is the selected (filled) one — reverse the
   foreground/background so the count stays legible on the dark fill. */
.rr-parent-tabs :deep(.v-tab--selected) .rr-parent-tab-badge {
  background: var(--ds-text-on-dark);
  color: var(--ds-brand-primary-dark);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
  animation: none;
}

@keyframes rr-badge-pulse {

  0%,
  100% {
    box-shadow: 0 0 0 3px var(--ds-brand-primary-light);
  }

  50% {
    box-shadow: 0 0 0 6px rgba(69, 182, 169, 0.15);
  }
}

/* ─── Filters ──────────────────────────────────────────────────────────── */

.rr-filters {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3);
  padding: var(--ds-space-4);
}

.rr-filters__chips {
  display: flex;
  gap: var(--ds-space-2);
  flex-wrap: wrap;
}

.rr-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border: 1px solid var(--ds-border-default);
  border-radius: 999px;
  background: transparent;
  font-size: var(--ds-text-sm);
  font-weight: 500;
  color: var(--ds-text-secondary);
  cursor: pointer;
  transition: all 150ms ease;
}

.rr-chip:hover {
  background: var(--ds-surface-inset);
  color: var(--ds-text-strong);
  border-color: var(--ds-border-strong);
}

.rr-chip:focus-visible {
  outline: none;
  box-shadow: var(--ds-shadow-focus);
}

.rr-chip--active {
  background: var(--ds-brand-primary-dark);
  border-color: var(--ds-brand-primary-dark);
  color: var(--ds-text-on-dark);
}

.rr-chip--active:hover {
  background: var(--ds-brand-primary-darker);
  border-color: var(--ds-brand-primary-darker);
  color: var(--ds-text-on-dark);
}

.rr-filters__inputs {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2);
}

.rr-filters__input {
  width: 100%;
}

.rr-filters__clear {
  align-self: flex-start;
  text-transform: none;
  letter-spacing: 0;
}

@media (min-width: 640px) {
  .rr-filters__inputs {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  .rr-filters__input {
    flex: 1 1 240px;
    max-width: 360px;
  }
}

/* ─── Loading ──────────────────────────────────────────────────────────── */

.rr-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-16) 0;
}

/* ─── Period selector (Completed) ──────────────────────────────────────── */

.rr-period {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2);
  padding: var(--ds-space-4) var(--ds-space-4) 0;
}

.rr-period__select {
  width: 100%;
}

.rr-period__date {
  width: 100%;
}

@media (min-width: 640px) {
  .rr-period {
    flex-direction: row;
    align-items: center;
  }

  .rr-period__select {
    flex: 0 1 260px;
  }

  .rr-period__date {
    flex: 0 1 180px;
  }
}

/* ─── Analytics ────────────────────────────────────────────────────────── */

.rr-analytics__loader {
  margin: var(--ds-space-4) var(--ds-space-4) 0;
}

.rr-analytics {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  padding: var(--ds-space-4);
}

/* Hero stats (3 headline numbers) — compact on mobile, generous on desktop */
.rr-hero-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: linear-gradient(135deg, var(--ds-brand-primary-light) 0%, var(--ds-surface-card) 100%);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-lg, 16px);
  padding: var(--ds-space-3) var(--ds-space-2);
}

@media (min-width: 640px) {
  .rr-hero-stats {
    gap: var(--ds-space-6);
    padding: var(--ds-space-5);
  }
}

.rr-hero-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 0 var(--ds-space-2);
}

.rr-hero-stat+.rr-hero-stat {
  border-left: 1px solid var(--ds-border-subtle);
}

@media (min-width: 640px) {
  .rr-hero-stat {
    gap: 6px;
    padding: 0;
  }

  .rr-hero-stat+.rr-hero-stat {
    padding-left: var(--ds-space-6);
  }
}

.rr-hero-stat__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ds-text-muted);
  line-height: 1.3;
}

@media (min-width: 640px) {
  .rr-hero-stat__label {
    font-size: var(--ds-text-xs);
  }
}

.rr-hero-stat__value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--ds-text-strong);
  line-height: 1.15;
  letter-spacing: var(--ds-tracking-tight);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 640px) {
  .rr-hero-stat__value {
    font-size: 2rem;
    line-height: 1.05;
    white-space: normal;
  }
}

@media (min-width: 900px) {
  .rr-hero-stat__value {
    font-size: 2.25rem;
  }
}

.rr-hero-stat__value--accent {
  color: var(--ds-brand-primary-darker);
}

.rr-hero-stat__unit {
  font-size: 0.7em;
  font-weight: 600;
  color: var(--ds-text-muted);
  margin-right: 1px;
}

@media (min-width: 640px) {
  .rr-hero-stat__unit {
    font-size: 0.65em;
  }
}

/* Outcome breakdowns */
.rr-outcomes {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--ds-space-3);
}

@media (min-width: 640px) {
  .rr-outcomes {
    grid-template-columns: repeat(2, 1fr);
  }
}

.rr-outcome {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--ds-space-4) var(--ds-space-4) var(--ds-space-4) var(--ds-space-5);
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md, 12px);
}

.rr-outcome::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10%;
  bottom: 10%;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: var(--ds-border-default);
}

.rr-outcome--positive::before {
  background: var(--ds-brand-primary-darker);
}

.rr-outcome--negative::before {
  background: #c8553d;
}

.rr-outcome__value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--ds-text-strong);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.rr-outcome__label {
  font-size: var(--ds-text-sm);
  font-weight: 600;
  color: var(--ds-text-primary);
}

.rr-outcome__breakdown {
  display: flex;
  flex-wrap: wrap;
  column-gap: var(--ds-space-3);
  row-gap: 2px;
  font-size: var(--ds-text-xs);
  color: var(--ds-text-muted);
  margin-top: 2px;
}

/* Secondary metrics grid */
.rr-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--ds-space-2);
}

@media (min-width: 520px) {
  .rr-metrics {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 820px) {
  .rr-metrics {
    grid-template-columns: repeat(4, 1fr);
  }
}

.rr-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--ds-space-3);
  background: var(--ds-surface-inset);
  border-radius: var(--ds-radius-md, 10px);
  min-width: 0;
}

.rr-metric__label {
  font-size: var(--ds-text-xs);
  font-weight: 500;
  color: var(--ds-text-muted);
  line-height: 1.3;
}

.rr-metric__value {
  font-size: var(--ds-text-xl);
  font-weight: 600;
  color: var(--ds-text-strong);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

.rr-metric__value--sm {
  font-size: var(--ds-text-md);
}

.rr-metric__unit {
  font-size: 0.7em;
  font-weight: 500;
  color: var(--ds-text-muted);
  margin: 0 1px 0 0;
}

/* Segment facts */
.rr-segments {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md, 12px);
  overflow: hidden;
}

@media (min-width: 640px) {
  .rr-segments {
    grid-template-columns: repeat(3, 1fr);
  }
}

.rr-segment {
  padding: var(--ds-space-3) var(--ds-space-4);
  border-bottom: 1px solid var(--ds-border-subtle);
}

.rr-segment:last-child {
  border-bottom: none;
}

@media (min-width: 640px) {
  .rr-segment {
    border-bottom: none;
    border-right: 1px solid var(--ds-border-subtle);
  }

  .rr-segment:last-child {
    border-right: none;
  }
}

.rr-segment__label {
  font-size: var(--ds-text-xs);
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ds-text-muted);
  margin-bottom: 2px;
}

.rr-segment__value {
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-2);
  flex-wrap: wrap;
  font-size: var(--ds-text-sm);
  color: var(--ds-text-primary);
}

.rr-segment__value strong {
  font-size: var(--ds-text-lg);
  font-weight: 700;
  color: var(--ds-text-strong);
  font-variant-numeric: tabular-nums;
}

.rr-segment__detail {
  color: var(--ds-text-muted);
  font-size: var(--ds-text-xs);
}

/* ─── Sub-tab pill strip ───────────────────────────────────────────────── */

.rr-subtabs {
  border-top: 1px solid var(--ds-border-subtle);
  background: var(--ds-surface-subtle);
}

.rr-subtabs__scroll {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: var(--ds-space-3) var(--ds-space-4);
}

.rr-subtabs__scroll::-webkit-scrollbar {
  display: none;
}

.rr-subtab {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-2);
  padding: 6px 12px 6px 14px;
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-default);
  border-radius: 999px;
  font-size: var(--ds-text-sm);
  font-weight: 500;
  color: var(--ds-text-secondary);
  white-space: nowrap;
  cursor: pointer;
  transition: all 150ms ease;
  flex-shrink: 0;
}

.rr-subtab:hover {
  background: var(--ds-brand-primary-light);
  border-color: var(--ds-brand-primary);
  color: var(--ds-text-strong);
}

.rr-subtab:focus-visible {
  outline: none;
  box-shadow: var(--ds-shadow-focus);
}

.rr-subtab--active {
  background: var(--ds-brand-primary-dark);
  border-color: var(--ds-brand-primary-dark);
  color: var(--ds-text-on-dark);
  box-shadow: var(--ds-shadow-sm);
}

.rr-subtab--active:hover {
  background: var(--ds-brand-primary-darker);
  border-color: var(--ds-brand-primary-darker);
  color: var(--ds-text-on-dark);
}

.rr-subtab--emphasis {
  border-color: var(--ds-brand-primary);
  color: var(--ds-brand-primary-darker);
  font-weight: 600;
}

.rr-subtab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  font-size: var(--ds-text-xs);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.rr-subtab--active .rr-subtab__count {
  background: rgba(255, 255, 255, 0.22);
  color: var(--ds-text-on-dark);
}

.rr-subtab--emphasis .rr-subtab__count {
  background: var(--ds-brand-primary);
  color: var(--ds-text-on-dark);
}

/* ─── Reservation list ─────────────────────────────────────────────────── */

.rr-list {
  padding: var(--ds-space-4) var(--ds-space-4) var(--ds-space-6);
  min-height: 320px;
}

.rr-list__item {
  margin-bottom: var(--ds-space-3);
}

.rr-list__item:last-child {
  margin-bottom: 0;
}

/* ─── Empty state ──────────────────────────────────────────────────────── */

.rr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-12) var(--ds-space-4);
  text-align: center;
}

.rr-empty__title {
  font-size: var(--ds-text-lg);
  font-weight: 600;
  color: var(--ds-text-strong);
  margin-top: var(--ds-space-2);
}

.rr-empty__sub {
  font-size: var(--ds-text-sm);
  color: var(--ds-text-muted);
  max-width: 340px;
  line-height: 1.45;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
